import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Building2, Globe, Calendar, Hash } from "lucide-react";
import { getCompany, getCompanyLogoUrl } from "@/services/firebase/company";
import { getDramasByCompany } from "@/services/firebase/drama";
import { Drama } from "@/types/video";
import { useDisplayAdjustments } from "@/hooks/useDisplayAdjustments";
import { dramaAdjustment } from "@/services/firebase/displayMetric";
import {
  AdminPage,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// 公司 Logo 顯示元件 — 同步 build URL,瀏覽器自動 disk-cache
function CompanyLogoLarge({ logo, name }: { logo?: string; name: string }) {
  const logoUrl = getCompanyLogoUrl(logo);

  if (!logoUrl) {
    return (
      <div className="w-24 h-24 bg-muted rounded-xl flex items-center justify-center">
        <Building2 className="w-12 h-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={name}
      width={96}
      height={96}
      loading="lazy"
      decoding="async"
      className="w-24 h-24 object-cover rounded-xl border bg-card"
    />
  );
}

const dramaColumns: AdminTableColumn[] = [
  { key: "drama_title", label: "劇名", sortable: true },
  { key: "drama_id", label: "Drama ID", hideBelow: "md" },
  { key: "isReleased", label: "狀態" },
  { key: "drama_play_times", label: "播放次數", sortable: true },
  { key: "created_at", label: "建立時間", hideBelow: "lg", sortable: true },
];

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: company, isLoading: loadingCompany, error: companyError } = useQuery({
    queryKey: ["company", id],
    queryFn: () => getCompany(id!),
    enabled: !!id,
  });

  const { data: displayAdjustments } = useDisplayAdjustments();

  const { data: dramas, isLoading: loadingDramas } = useQuery({
    queryKey: ["dramas-by-company", id],
    queryFn: () => getDramasByCompany(id!),
    enabled: !!id,
  });

  if (loadingCompany) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (companyError || !company) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={`載入製作公司時發生錯誤：${
            companyError instanceof Error ? companyError.message : "找不到公司"
          }`}
        />
        <Button variant="outline" onClick={() => navigate("/companies")} className="mt-4">
          <ArrowLeft className="h-4 w-4" />
          返回列表
        </Button>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      {/* Header：返回 + 標題 + 編輯按鈕 */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/companies")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {company.company_name || "(未命名)"}
            </h1>
            <p className="mt-0.5 text-xs font-mono text-muted-foreground">
              {company.company_id || company.id}
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to={`/companies/${company.id}/edit`}>
            <Pencil className="h-4 w-4" />
            編輯
          </Link>
        </Button>
      </div>

      {/* 基本資訊卡片 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-6">
            <CompanyLogoLarge logo={company.company_logo} name={company.company_name || ""} />

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  公司 ID
                </div>
                <div className="font-mono">{company.company_id || company.id}</div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">公司名稱</div>
                <div>{company.company_name || "-"}</div>
              </div>

              <div className="sm:col-span-2">
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  網站
                </div>
                {company.website ? (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                  >
                    {company.website}
                  </a>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  建立時間
                </div>
                <div className="text-muted-foreground">
                  {company.created_at
                    ? new Date(company.created_at).toLocaleString("zh-TW")
                    : "-"}
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  最後更新
                </div>
                <div className="text-muted-foreground">
                  {company.updated_at
                    ? new Date(company.updated_at).toLocaleString("zh-TW")
                    : "-"}
                </div>
              </div>
            </div>
          </div>

          {company.description && (
            <div className="mt-6 pt-6 border-t">
              <div className="text-xs text-muted-foreground mb-2">公司描述</div>
              <p className="text-sm whitespace-pre-wrap">{company.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 旗下劇集 */}
      <Card>
        <CardHeader>
          <CardTitle>旗下劇集 {dramas ? `(${dramas.length})` : ""}</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDramas ? (
            <AdminLoadingSkeleton rows={3} />
          ) : (
            <AdminDataTable
              columns={dramaColumns}
              data={dramas || []}
              getRowId={(item) => item.id || ""}
              onRowClick={(drama) => navigate(`/dramas/${drama.id}`)}
              emptyTitle="此公司目前尚無劇集"
              defaultSort={{ key: "created_at", dir: "desc" }}
              renderCell={(drama: Drama, col) => {
                switch (col.key) {
                  case "drama_title":
                    return (
                      <span className="text-sm font-medium">{drama.drama_title || "-"}</span>
                    );
                  case "drama_id":
                    return (
                      <span className="text-xs font-mono text-muted-foreground">
                        {drama.drama_id || drama.id}
                      </span>
                    );
                  case "isReleased":
                    return drama.isReleased ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                        已上架
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        未上架
                      </span>
                    );
                  case "drama_play_times":
                    return (
                      <span className="text-sm tabular-nums">
                        {(
                          (drama.drama_play_times || 0) -
                          dramaAdjustment(displayAdjustments, drama.id, "drama_play_times")
                        ).toLocaleString()}
                      </span>
                    );
                  case "created_at":
                    return (
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {drama.created_at
                          ? new Date(drama.created_at).toLocaleDateString("zh-TW")
                          : "-"}
                      </span>
                    );
                  default:
                    return null;
                }
              }}
            />
          )}
        </CardContent>
      </Card>
    </AdminPage>
  );
}
